import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import './ListingsGrid.css';
import RoomsExpandedFilters, { initialRoomsExpandedFilters } from './RoomsExpandedFilters';
import FoundFlatExpandedFilters, { initialFoundFlatExpandedFilters } from './FoundFlatExpandedFilters';
import SearchingExpandedFilters, { initialSearchingExpandedFilters } from './SearchingExpandedFilters';
import ListingCard, { getCardImage } from './ListingCard';
import ExpandedCategoryGrid, { expandedCategoryTitles } from './ExpandedCategoryGrid';

import { getLocationDisplay, cities, regions } from '../constants/locations';
import { getAccommodationTypeWithTenants } from '../constants/accommodationTypes';
import { getAmenitiesAr, getAmenitiesEn, getAmenitiesValues } from '../constants/amenities';
import { getTimeAgo } from '../constants/timeUtils';

import { db, auth } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';

// ─── حجم الصفحة المعروضة للمستخدم ───────────────────────────────────────────
const PAGE_SIZE = 12;

// ─── حجم الـ batch اللي بنجيبه من Firestore في كل مرة ──────────────────────
// نجيب أكثر عشان بعد الـ filter مش يقل عن 12
const FETCH_SIZE = 20;

// ─── شاشة "Show More" الموسّعة (جريد كامل + صفحات) ─────────────────────────
const EXPANDED_PAGE_SIZE = 8; // عدد الإعلانات في كل صفحة
const EXPANDED_FETCH_LIMIT = 200; // أقصى عدد نجيبه من Firestore للقسم المختار


// ─── Count applied expanded filters (same rules as filter panels) ───────────
const countRoomsFilters = (f) => {
  if (!f) return 0;
  let n = 0;
  n += (f.accommodationTypes || []).length;
  n += (f.tenants || []).length;
  if (f.priceRange?.min !== '' && f.priceRange?.min != null) n += 1;
  if (f.priceRange?.max !== '' && f.priceRange?.max != null) n += 1;
  n += (f.priceDuration || []).length;
  n += (f.amenities || []).length;
  n += (f.smokingPolicy || []).length;
  n += (f.visitorsPolicy || []).length;
  n += (f.petsPolicy || []).length;
  return n;
};

const countFoundFlatFilters = (f) => {
  if (!f) return 0;
  let n = 0;
  n += (f.gender || []).length;
  if (f.priceRange?.min !== '' && f.priceRange?.min != null) n += 1;
  if (f.priceRange?.max !== '' && f.priceRange?.max != null) n += 1;
  n += (f.desiredTeamSize || []).length;
  n += (f.openToOtherFlat || []).length;
  n += (f.lifestyle || []).length;
  return n;
};

const countSearchingFilters = (f) => {
  if (!f) return 0;
  let n = 0;
  n += (f.gender || []).length;
  if (f.priceRange?.min !== '' && f.priceRange?.min != null) n += 1;
  if (f.priceRange?.max !== '' && f.priceRange?.max != null) n += 1;
  n += (f.desiredTeamSize || []).length;
  n += (f.lifestyle || []).length;
  return n;
};

// ─── Transform functions ──────────────────────────────────────────────────────

const transformAdData = (id, data) => ({
  id,
  userId: data.userId,
  image: data.ad_images?.[0],
  images: data.ad_images || [],
  title: data.accommodation_type,
  location: {
    ar: getLocationDisplay(data.city, data.region, 'ar'),
    en: getLocationDisplay(data.city, data.region, 'en'),
    city: data.city,
    region: data.region,
  },
  price: data.price,
  type: {
    ar: getAccommodationTypeWithTenants(data.accommodation_type, data.tenants_policy, 'ar'),
    en: getAccommodationTypeWithTenants(data.accommodation_type, data.tenants_policy, 'en'),
    value: data.accommodation_type,
  },
  phoneNumber: data.ad_contact_phone,
  whatsappNumber: data.ad_contact_whatsapp,
  amenities: {
    ar: getAmenitiesAr(data.amenities),
    en: getAmenitiesEn(data.amenities),
    values: getAmenitiesValues(data.amenities),
  },
  description: data.ad_description,
  timeSincePosted: {
    ar: getTimeAgo(data.createdAt, 'ar'),
    en: getTimeAgo(data.createdAt, 'en'),
  },
  createdAtTimestamp: data.createdAt?.toDate?.() || new Date(data.createdAt),
  tenants: data.tenants_policy,
  visitorsPolicy: data.visitors_policy,
  petsPolicy: data.pets_policy,
  priceDuration: data.price_type,
  smokingPolicy: data.smoking_policy,
  insurance: data.insurance,
  priceIncludesBills: data.price_includes_bills || false,
  latitude: data.latitude,
  longitude: data.longitude,
  status: data.status,
  adType: 'regular',
});

const transformTeamUpData = (id, data) => {
  const hasFlat = data.has_flat === 'yes';

  // Location: hasFlat → single region (string), looking-for → multiple preferred_regions (array)
  const location = hasFlat
    ? {
        ar: getLocationDisplay(data.city, data.region, 'ar'),
        en: getLocationDisplay(data.city, data.region, 'en'),
        city: data.city,
        region: data.region,
      }
    : {
        ar: Array.isArray(data.preferred_regions)
          ? formatMultiLocation(data.preferred_city, data.preferred_regions, 'ar')
          : getLocationDisplay(data.preferred_city, data.preferred_regions, 'ar'),
        en: Array.isArray(data.preferred_regions)
          ? formatMultiLocation(data.preferred_city, data.preferred_regions, 'en')
          : getLocationDisplay(data.preferred_city, data.preferred_regions, 'en'),
        city: data.preferred_city,
        region: data.preferred_regions,
      };

  return {
    id,
    userId: data.userId,
    personalName: data.personal_name,
    profilePhoto: data.profile_photo,
    gender: data.gender,
    age: data.age,
    desiredTeamSize: data.desired_team_size,
    currentTeamSize: data.current_team_size,
    hasFlat: data.has_flat,
    location,
    price: hasFlat ? data.monthly_rent : data.budget_per_person,
    priceDuration: 'monthly',
    insurance: hasFlat ? data.refundable_insurance : null,
    moveInDate: hasFlat ? data.move_in_date : data.preferred_move_in_date,
    description: hasFlat ? data.flat_description : data.looking_for_description,
    lifestyle: data.lifestyle || [],
    bedrooms: data.bedrooms != null ? String(data.bedrooms) : '',
    bathrooms: data.bathrooms != null ? String(data.bathrooms) : '',
    openToOtherFlat: data.open_to_other_flat || '',
    phoneNumber: data.contact_phone,
    whatsappNumber: data.contact_whatsapp,
    timeSincePosted: {
      ar: getTimeAgo(data.createdAt, 'ar'),
      en: getTimeAgo(data.createdAt, 'en'),
    },
    createdAtTimestamp: data.createdAt?.toDate?.() || new Date(data.createdAt),
    latitude: data.latitude,
    longitude: data.longitude,
    status: data.status,
    adType: 'team_up',
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

function ListingsGrid({
  currentLanguage,
  getLocalizedText,
  city = '',
  region = '',
  selectedLocation,
  user,
  isMobile,
  activeHeroFilter = 'all',
  onHeroFilterChange,
  expandedCategory = null,
  onExpandedCategoryChange,
}) {
  // الإعلانات المعروضة للمستخدم حالياً
  const [displayedListings, setDisplayedListings] = useState([]);

  // buffer: الإعلانات اللي اتجلبت من Firestore بس لسه مش متعرضتش
  const [buffer, setBuffer] = useState([]);

  // rawPool: كل الدوكيومنتس اللي اتجابت من Firestore لحد دلوقتي وعدّت الفلترة
  // اللي في الـ query نفسه (status + city + region) — لسه من غير applyFilters
  // الخاص بالفلاتر اللي بتتعمل في الميموري (hero tab, distance sort, باقي الفلاتر).
  // ده اللي بيخلينا نعيد الفلترة/الترتيب من غير ما نعمل fetch جديد من Firestore.
  const [rawPool, setRawPool] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // cursors لـ Firestore pagination
  const [lastVisibleAd, setLastVisibleAd] = useState(null);
  const [lastVisibleTeamUp, setLastVisibleTeamUp] = useState(null);

  // هل في المزيد في Firestore لكل collection على حدة (عشان نقدر نعمل skip لـ collection مش محتاجينها)
  const [hasMoreAds, setHasMoreAds] = useState(true);
  const [hasMoreTeamUps, setHasMoreTeamUps] = useState(true);

  // أنواع الدوكيومنتس اللي اتجابت بالفعل للـ city/region الحاليين — عشان
  // لما المستخدم يغيّر الـ hero tab منغير ما نعيد fetch لنوع موجود في الـ pool
  const loadedTypesRef = useRef({ ads: false, teamUps: false });
  const rawPoolRef = useRef([]);
  const lastVisibleAdRef = useRef(null);
  const lastVisibleTeamUpRef = useRef(null);
  useEffect(() => { rawPoolRef.current = rawPool; }, [rawPool]);
  useEffect(() => { lastVisibleAdRef.current = lastVisibleAd; }, [lastVisibleAd]);
  useEffect(() => { lastVisibleTeamUpRef.current = lastVisibleTeamUp; }, [lastVisibleTeamUp]);

  // هل في المزيد مناسب للـ hero tab الحالي؟
  const firestoreHasMore =
    ((activeHeroFilter === 'all' || activeHeroFilter === 'rooms') && hasMoreAds) ||
    ((activeHeroFilter === 'all' || activeHeroFilter === 'teamup') && hasMoreTeamUps);

  // ─── شاشة "Show More" الموسّعة (controlled from App via expandedCategory / onExpandedCategoryChange) ──
  const [expandedListings, setExpandedListings] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [expandedError, setExpandedError] = useState(null);
  const [expandedPage, setExpandedPage] = useState(1);
  // raw items fetched for expanded view (before room-filter apply)
  const [expandedRawListings, setExpandedRawListings] = useState([]);
  const [roomsExpandedFilters, setRoomsExpandedFilters] = useState(initialRoomsExpandedFilters);
  const [showRoomsFilters, setShowRoomsFilters] = useState(false);
  const [foundFlatExpandedFilters, setFoundFlatExpandedFilters] = useState(initialFoundFlatExpandedFilters);
  const [showFoundFlatFilters, setShowFoundFlatFilters] = useState(false);
  const [searchingExpandedFilters, setSearchingExpandedFilters] = useState(initialSearchingExpandedFilters);
  const [showSearchingFilters, setShowSearchingFilters] = useState(false);

  // مجموعة معرّفات إعلانات المستخدم الحالي المفضلة (تُجلب مرة واحدة فقط)
  const [favouriteIds, setFavouriteIds] = useState(new Set());

  // كاش لأسماء أصحاب الإعلانات: { userId: firstName } — يتراكم عبر الصفحات بدل ما يتجاب من جديد
  const [posterNames, setPosterNames] = useState({});
  const posterNamesRef = useRef({});
  useEffect(() => {
    posterNamesRef.current = posterNames;
  }, [posterNames]);

  // refs للسكرول الأفقي بتاع كل صف (غرف / لقيت شقة / بدور على شقة)
  const roomsRowRef = useRef(null);
  const foundFlatRowRef = useRef(null);
  const searchingRowRef = useRef(null);

  const scrollRow = (rowRef, direction) => {
    if (!rowRef.current) return;
    const amount = rowRef.current.clientWidth * 0.85;
    rowRef.current.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  const getFirstName = (fullName) => (fullName ? fullName.trim().split(' ')[0] : '');

  // تبديل حالة المفضلة لكارت معيّن (نفس منطق Ad.js)
  const toggleFavorite = async (listingId) => {
    if (!auth.currentUser) {
      alert(getLocalizedText('يجب تسجيل الدخول لإضافة إلى المفضلة', 'Please login to add to favourites'));
      return;
    }

    const isCurrentlyFavorited = favouriteIds.has(listingId);
    const userRef = doc(db, 'verified_users', auth.currentUser.uid);

    // تحديث تفاؤلي فوري في الواجهة
    setFavouriteIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlyFavorited) next.delete(listingId);
      else next.add(listingId);
      return next;
    });

    try {
      if (isCurrentlyFavorited) {
        await updateDoc(userRef, { favourite_ads_id: arrayRemove(listingId) });
      } else {
        await setDoc(userRef, { favourite_ads_id: arrayUnion(listingId) }, { merge: true });
      }
    } catch (err) {
      console.error('Error toggling favourite:', err);
      // ارجع للحالة القديمة لو الكتابة فشلت
      setFavouriteIds((prev) => {
        const next = new Set(prev);
        if (isCurrentlyFavorited) next.add(listingId);
        else next.delete(listingId);
        return next;
      });
      alert(getLocalizedText('حدث خطأ، حاول مرة أخرى', 'Error, please try again'));
    }
  };

  // جلب أسماء أصحاب الإعلانات لدفعة من اللستنجز، دفعة واحدة، مع تجنب إعادة جلب اللي موجود في الكاش
  const fetchPosterNamesForBatch = async (batchListings) => {
    const distinctUserIds = [
      ...new Set(batchListings.map((l) => l.userId).filter(Boolean)),
    ].filter((id) => !(id in posterNamesRef.current));

    if (distinctUserIds.length === 0) return;

    try {
      const results = await Promise.all(
        distinctUserIds.map(async (userId) => {
          try {
            const userSnap = await getDoc(doc(db, 'verified_users', userId));
            return [userId, userSnap.exists() ? getFirstName(userSnap.data().fullName) : ''];
          } catch (err) {
            console.error('Error fetching poster name for', userId, err);
            return [userId, ''];
          }
        })
      );

      setPosterNames((prev) => {
        const next = { ...prev };
        results.forEach(([userId, firstName]) => {
          next[userId] = firstName;
        });
        return next;
      });
    } catch (err) {
      console.error('Error batch-fetching poster names:', err);
    }
  };

  // جلب مفضلة المستخدم الحالي مرة واحدة فقط (مش لكل كارت)
  useEffect(() => {
    if (!user) {
      setFavouriteIds(new Set());
      return;
    }

    const fetchFavouriteIds = async () => {
      try {
        const userSnap = await getDoc(doc(db, 'verified_users', user.uid));
        if (userSnap.exists()) {
          setFavouriteIds(new Set(userSnap.data().favourite_ads_id || []));
        } else {
          setFavouriteIds(new Set());
        }
      } catch (err) {
        console.error('Error fetching favourite ids:', err);
      }
    };

    fetchFavouriteIds();
  }, [user]);

  const applyFilters = (items) => {
    return items.filter((listing) => {
      if (activeHeroFilter === 'rooms' && listing.adType !== 'regular') return false;
      if (activeHeroFilter === 'teamup' && listing.adType !== 'team_up') return false;

      // Defense-in-depth: city/region already constrained in Firestore query
      if (city && listing.location.city !== city) return false;

      if (region) {
        const listingRegion = listing.location.region;
        if (Array.isArray(listingRegion)) {
          if (!listingRegion.includes(region)) return false;
        } else {
          if (listingRegion !== region) return false;
        }
      }

      return true;
    });
  };

  const sortItems = (items) => {
    if (selectedLocation?.lat && selectedLocation?.lng) {
      return [...items].sort((a, b) => {
        const distA = calculateDistance(
          selectedLocation.lat, selectedLocation.lng, a.latitude, a.longitude
        );
        const distB = calculateDistance(
          selectedLocation.lat, selectedLocation.lng, b.latitude, b.longitude
        );
        return distA - distB;
      });
    }
    return [...items].sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp);
  };

  // ─── فلترة قسم الـ "Show More" الموسّع (نفس فلاتر الفلاتر العادية، من غير activeHeroFilter) ──
  const applyExpandedFilters = (items, categoryKey, extraFilters = null) => {
    return items.filter((listing) => {
      if (city && listing.location.city !== city) return false;

      if (region) {
        const listingRegion = listing.location.region;
        if (Array.isArray(listingRegion)) {
          if (!listingRegion.includes(region)) return false;
        } else if (listingRegion !== region) {
          return false;
        }
      }

      // Room-only filters (used on the rooms expanded page)
      if (categoryKey === 'rooms') {
        const roomFilters = extraFilters || roomsExpandedFilters;
        const {
          accommodationTypes = [],
          tenants = [],
          priceRange = { min: '', max: '' },
          priceDuration = [],
          amenities = [],
          smokingPolicy = [],
          visitorsPolicy = [],
          petsPolicy = [],
        } = roomFilters || {};

        if (accommodationTypes.length > 0 && !accommodationTypes.includes(listing.type?.value)) {
          return false;
        }
        if (tenants.length > 0 && !tenants.includes(listing.tenants)) {
          return false;
        }
        const price = listing.price;
        if (priceRange.min !== '' && priceRange.min != null && price < parseFloat(priceRange.min)) {
          return false;
        }
        if (priceRange.max !== '' && priceRange.max != null && price > parseFloat(priceRange.max)) {
          return false;
        }
        if (priceDuration.length > 0 && !priceDuration.includes(listing.priceDuration)) {
          return false;
        }
        if (amenities.length > 0) {
          const listingAmenities = listing.amenities?.values || [];
          if (!amenities.every((a) => listingAmenities.includes(a))) {
            return false;
          }
        }
        if (smokingPolicy.length > 0 && !smokingPolicy.includes(listing.smokingPolicy)) {
          return false;
        }
        if (visitorsPolicy.length > 0 && !visitorsPolicy.includes(listing.visitorsPolicy)) {
          return false;
        }
        if (petsPolicy.length > 0 && !petsPolicy.includes(listing.petsPolicy)) {
          return false;
        }
      }

      // Found-a-flat team-up filters (second row expanded page)
      if (categoryKey === 'foundFlat') {
        const ff = extraFilters || foundFlatExpandedFilters;
        const {
          gender = [],
          priceRange = { min: '', max: '' },
          desiredTeamSize = [],
          bedrooms = [],
          bathrooms = [],
          lifestyle = [],
          openToOtherFlat = [],
        } = ff || {};

        if (gender.length > 0 && !gender.includes(listing.gender)) {
          return false;
        }

        const price = listing.price;
        if (priceRange.min !== '' && priceRange.min != null && Number.isFinite(parseFloat(priceRange.min)) && price < parseFloat(priceRange.min)) {
          return false;
        }
        if (priceRange.max !== '' && priceRange.max != null && Number.isFinite(parseFloat(priceRange.max)) && price > parseFloat(priceRange.max)) {
          return false;
        }

        if (desiredTeamSize.length > 0) {
          const size = Number(listing.desiredTeamSize);
          const match = desiredTeamSize.some((v) => {
            if (v === '5') return size >= 5;
            return size === Number(v);
          });
          if (!match) return false;
        }

        if (bedrooms.length > 0 && !bedrooms.includes(String(listing.bedrooms))) {
          return false;
        }
        if (bathrooms.length > 0 && !bathrooms.includes(String(listing.bathrooms))) {
          return false;
        }

        if (openToOtherFlat.length > 0 && !openToOtherFlat.includes(listing.openToOtherFlat)) {
          return false;
        }

        if (lifestyle.length > 0) {
          const listingLifestyle = listing.lifestyle || [];
          if (!lifestyle.every((l) => listingLifestyle.includes(l))) {
            return false;
          }
        }
      }

      // Searching-for-flat team-up filters (third row expanded page)
      if (categoryKey === 'searching') {
        const sf = extraFilters || searchingExpandedFilters;
        const {
          gender = [],
          priceRange = { min: '', max: '' },
          desiredTeamSize = [],
          lifestyle = [],
        } = sf || {};

        if (gender.length > 0 && !gender.includes(listing.gender)) {
          return false;
        }

        const price = listing.price;
        if (priceRange.min !== '' && priceRange.min != null && Number.isFinite(parseFloat(priceRange.min)) && price < parseFloat(priceRange.min)) {
          return false;
        }
        if (priceRange.max !== '' && priceRange.max != null && Number.isFinite(parseFloat(priceRange.max)) && price > parseFloat(priceRange.max)) {
          return false;
        }

        if (desiredTeamSize.length > 0) {
          const size = Number(listing.desiredTeamSize);
          const match = desiredTeamSize.some((v) => {
            if (v === '5') return size >= 5;
            return size === Number(v);
          });
          if (!match) return false;
        }

        if (lifestyle.length > 0) {
          const listingLifestyle = listing.lifestyle || [];
          if (!lifestyle.every((l) => listingLifestyle.includes(l))) {
            return false;
          }
        }
      }

      return true;
    });
  };

  // ─── جلب كل إعلانات قسم معيّن لما المستخدم يدوس Show More ──────────────────
  const openExpandedCategory = async (categoryKey) => {
    if (onExpandedCategoryChange) onExpandedCategoryChange(categoryKey);
    setExpandedLoading(true);
    setExpandedError(null);
    setExpandedPage(1);
    setExpandedListings([]);
    setExpandedRawListings([]);
    setRoomsExpandedFilters(initialRoomsExpandedFilters);
    setFoundFlatExpandedFilters(initialFoundFlatExpandedFilters);
    setSearchingExpandedFilters(initialSearchingExpandedFilters);
    setShowRoomsFilters(false);
    setShowFoundFlatFilters(false);
    setShowSearchingFilters(false);

    try {
      let items = [];

      if (categoryKey === 'rooms') {
        const constraints = [where('status', '==', 'active')];
        if (city) constraints.push(where('city', '==', city));
        if (city && region) constraints.push(where('region', '==', region));
        constraints.push(orderBy('createdAt', 'desc'));
        constraints.push(limit(EXPANDED_FETCH_LIMIT));

        const snap = await getDocs(query(collection(db, 'ads'), ...constraints));
        items = snap.docs.map((d) => transformAdData(d.id, d.data()));
      } else {
        const constraints = [where('status', '==', 'active')];
        constraints.push(orderBy('createdAt', 'desc'));
        constraints.push(limit(EXPANDED_FETCH_LIMIT));

        const snap = await getDocs(query(collection(db, 'team_up_ads'), ...constraints));
        const allTeamUps = snap.docs.map((d) => transformTeamUpData(d.id, d.data()));
        items = allTeamUps.filter((l) =>
          categoryKey === 'foundFlat' ? l.hasFlat === 'yes' : l.hasFlat !== 'yes'
        );
      }

      setExpandedRawListings(items);
      const initialExtra =
        categoryKey === 'foundFlat'
          ? initialFoundFlatExpandedFilters
          : categoryKey === 'searching'
            ? initialSearchingExpandedFilters
            : initialRoomsExpandedFilters;
      const filtered = applyExpandedFilters(items, categoryKey, initialExtra);
      const sorted = sortItems(filtered);
      setExpandedListings(sorted);
      fetchPosterNamesForBatch(sorted);
    } catch (err) {
      console.error('Error fetching category listings:', err);
      setExpandedError(err.message);
    } finally {
      setExpandedLoading(false);
    }
  };

  const closeExpandedCategory = () => {
    if (onExpandedCategoryChange) onExpandedCategoryChange(null);
    if (onHeroFilterChange) onHeroFilterChange('all');
    setExpandedListings([]);
    setExpandedRawListings([]);
    setRoomsExpandedFilters(initialRoomsExpandedFilters);
    setShowRoomsFilters(false);
    setFoundFlatExpandedFilters(initialFoundFlatExpandedFilters);
    setShowFoundFlatFilters(false);
    setSearchingExpandedFilters(initialSearchingExpandedFilters);
    setShowSearchingFilters(false);
    setExpandedPage(1);
    setExpandedError(null);
  };

  const handleApplyRoomsFilters = (nextFilters) => {
    setRoomsExpandedFilters(nextFilters);
    setExpandedPage(1);
    const filtered = applyExpandedFilters(expandedRawListings, 'rooms', nextFilters);
    const sorted = sortItems(filtered);
    setExpandedListings(sorted);
  };

  const handleApplyFoundFlatFilters = (nextFilters) => {
    setFoundFlatExpandedFilters(nextFilters);
    setExpandedPage(1);
    const filtered = applyExpandedFilters(expandedRawListings, 'foundFlat', nextFilters);
    const sorted = sortItems(filtered);
    setExpandedListings(sorted);
  };

  const handleApplySearchingFilters = (nextFilters) => {
    setSearchingExpandedFilters(nextFilters);
    setExpandedPage(1);
    const filtered = applyExpandedFilters(expandedRawListings, 'searching', nextFilters);
    const sorted = sortItems(filtered);
    setExpandedListings(sorted);
  };

  const handleExpandedPageChange = (newPage) => {
    setExpandedPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ─── جلب batch من Firestore ──────────────────────────────────────────────────
  // ملحوظة: كل combination من (status alone / status+city / status+city+region)
  // محتاج composite index مستقل في Firebase Console (راجع القائمة اللي بعتّها).
  // heroFilter: 'all' | 'rooms' | 'teamup' — نتخطى الـ collection اللي الـ tab الحالي هيستبعدها.
  const fetchFromFirestore = async (lastAd, lastTeamUp, city, region, heroFilter = 'all') => {
    let newAds = [];
    let newTeamUps = [];
    let nextLastAd = lastAd;
    let nextLastTeamUp = lastTeamUp;
    let adsHasMore = null;      // null = لم يُستعلم عن هذا الـ collection في هذه الجولة
    let teamUpsHasMore = null;

    const needAds = heroFilter === 'all' || heroFilter === 'rooms';
    const needTeamUps = heroFilter === 'all' || heroFilter === 'teamup';

    // ── ads: city و region كلاهما string equality ──
    if (needAds) {
      const adsConstraints = [where('status', '==', 'active')];
      if (city) adsConstraints.push(where('city', '==', city));
      if (city && region) adsConstraints.push(where('region', '==', region));
      adsConstraints.push(orderBy('createdAt', 'desc'));
      if (lastAd) adsConstraints.push(startAfter(lastAd));
      adsConstraints.push(limit(FETCH_SIZE));

      const adsQuery = query(collection(db, 'ads'), ...adsConstraints);
      const snapshotAds = await getDocs(adsQuery);
      newAds = snapshotAds.docs.map((doc) => transformAdData(doc.id, doc.data()));
      if (snapshotAds.docs.length > 0) {
        nextLastAd = snapshotAds.docs[snapshotAds.docs.length - 1];
      }
      adsHasMore = snapshotAds.docs.length === FETCH_SIZE;
    }

    // ── team_up_ads: location هنا مقسومة على اتنين field مختلفين حسب has_flat
    // (city/region للي عنده شقة، preferred_city/preferred_regions للي بيدور)
    // فمفيش where() واحد يغطي الحالتين مع بعض، فبنجيب على status بس هنا
    // وبنسيب الفلترة بالمدينة/المنطقة تحصل في applyFilters (client-side).
    if (needTeamUps) {
      const teamUpsConstraints = [where('status', '==', 'active')];
      teamUpsConstraints.push(orderBy('createdAt', 'desc'));
      if (lastTeamUp) teamUpsConstraints.push(startAfter(lastTeamUp));
      teamUpsConstraints.push(limit(FETCH_SIZE));

      const teamUpsQuery = query(collection(db, 'team_up_ads'), ...teamUpsConstraints);
      const snapshotTeamUps = await getDocs(teamUpsQuery);
      newTeamUps = snapshotTeamUps.docs.map((doc) => transformTeamUpData(doc.id, doc.data()));
      if (snapshotTeamUps.docs.length > 0) {
        nextLastTeamUp = snapshotTeamUps.docs[snapshotTeamUps.docs.length - 1];
      }
      teamUpsHasMore = snapshotTeamUps.docs.length === FETCH_SIZE;
    }

    // هنا بس بنرتب بالـ createdAt (مش بالـ selectedLocation) عشان ده ترتيب
    // الـ raw pool الخام؛ الترتيب بالمسافة بيتعمل بعدين في recomputeDisplayFromPool
    const combined = [...newAds, ...newTeamUps].sort(
      (a, b) => b.createdAtTimestamp - a.createdAtTimestamp
    );

    return {
      combined,
      nextLastAd,
      nextLastTeamUp,
      adsFetched: needAds,
      teamUpsFetched: needTeamUps,
      adsHasMore,
      teamUpsHasMore,
    };
  };

  // ─── إعادة فلترة/ترتيب الـ rawPool في الميموري بس (من غير أي fetch جديد) ────
  // ده بيتنادى لما فلاتر client-only تتغير: activeHeroFilter, selectedLocation,
  // accommodationTypes, tenants, priceRange, priceDuration.
  const recomputeDisplayFromPool = (pool) => {
    const filtered = applyFilters(pool);
    const sorted = sortItems(filtered);
    setDisplayedListings(sorted.slice(0, PAGE_SIZE));
    setBuffer(sorted.slice(PAGE_SIZE));
    fetchPosterNamesForBatch(filtered);
  };

  // ─── Initial load ─────────────────────────────────────────────────────────────
  // بيتنادى بس لما city/region يتغيروا (Effect A) — لأن دول الوحيدين اللي فعلاً
  // بيغيّروا مين الدوكيومنتس المطلوبة من Firestore.
  // بنمرّر activeHeroFilter عشان نتخطى الـ collection اللي الـ tab الحالي مش محتاجها.
  const loadInitialListings = async () => {
    setLoading(true);
    setError(null);
    setDisplayedListings([]);
    setBuffer([]);
    setRawPool([]);
    setLastVisibleAd(null);
    setLastVisibleTeamUp(null);
    setHasMoreAds(true);
    setHasMoreTeamUps(true);
    loadedTypesRef.current = { ads: false, teamUps: false };

    try {
      const {
        combined,
        nextLastAd,
        nextLastTeamUp,
        adsFetched,
        teamUpsFetched,
        adsHasMore,
        teamUpsHasMore,
      } = await fetchFromFirestore(null, null, city, region, activeHeroFilter);

      if (adsFetched) {
        loadedTypesRef.current.ads = true;
        if (adsHasMore !== null) setHasMoreAds(adsHasMore);
      }
      if (teamUpsFetched) {
        loadedTypesRef.current.teamUps = true;
        if (teamUpsHasMore !== null) setHasMoreTeamUps(teamUpsHasMore);
      }

      setRawPool(combined);
      recomputeDisplayFromPool(combined);

      setLastVisibleAd(nextLastAd);
      setLastVisibleTeamUp(nextLastTeamUp);
    } catch (err) {
      console.error('Error fetching initial ads:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Show More ────────────────────────────────────────────────────────────────
  const loadMoreListings = async () => {
    if (loadingMore) return;

    // لو في buffer كافي نعرض منه مباشرة
    if (buffer.length >= PAGE_SIZE) {
      setDisplayedListings((prev) => [...prev, ...buffer.slice(0, PAGE_SIZE)]);
      setBuffer((prev) => prev.slice(PAGE_SIZE));
      return;
    }

    // لو مفيش Firestore pages تانية للـ tab الحالي، اعرض اللي في البuffer حتى لو أقل من 12
    if (!firestoreHasMore) {
      if (buffer.length > 0) {
        setDisplayedListings((prev) => [...prev, ...buffer]);
        setBuffer([]);
      }
      return;
    }

    // محتاج نجيب batch جديد من Firestore (بس للـ collection(s) المناسبة للـ tab)
    setLoadingMore(true);
    setError(null);

    try {
      const {
        combined,
        nextLastAd,
        nextLastTeamUp,
        adsFetched,
        teamUpsFetched,
        adsHasMore,
        teamUpsHasMore,
      } = await fetchFromFirestore(
        lastVisibleAd,
        lastVisibleTeamUp,
        city,
        region,
        activeHeroFilter
      );

      // نضيف الدوكيومنتس الجديدة لـ rawPool عشان أي in-memory recompute بعد
      // كده (hero tab / selectedLocation / باقي الفلاتر) يلاقيها من غير fetch جديد
      const existingIds = new Set(rawPool.map((l) => l.id));
      const uniqueNew = combined.filter((l) => !existingIds.has(l.id));
      const updatedPool = [...rawPool, ...uniqueNew];
      setRawPool(updatedPool);

      if (adsFetched) {
        loadedTypesRef.current.ads = true;
        if (adsHasMore !== null) setHasMoreAds(adsHasMore);
        setLastVisibleAd(nextLastAd);
      }
      if (teamUpsFetched) {
        loadedTypesRef.current.teamUps = true;
        if (teamUpsHasMore !== null) setHasMoreTeamUps(teamUpsHasMore);
        setLastVisibleTeamUp(nextLastTeamUp);
      }

      const filtered = applyFilters(uniqueNew);

      // ندمج البuffer القديم مع الجديد
      const merged = sortItems([...buffer, ...filtered]);

      // نعرض أول 12 ونحط الباقي في buffer
      setDisplayedListings((prev) => [...prev, ...merged.slice(0, PAGE_SIZE)]);
      setBuffer(merged.slice(PAGE_SIZE));

      // جلب أسماء أصحاب الإعلانات الجديدة فقط (اللي لسه مش في الكاش)
      fetchPosterNamesForBatch(filtered);
    } catch (err) {
      console.error('Error fetching more ads:', err);
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  // ─── Effects ──────────────────────────────────────────────────────────────────

  // Effect A: بيعمل fetch جديد من Firestore — بس لما city أو region يتغيروا،
  // لأنهم دول الوحيدين اللي فعلاً بيغيروا مين الدوكيومنتس المطلوبة.
  // بيستخدم activeHeroFilter الحالي عشان يتخطى الـ collection اللي مش محتاجينها.
  useEffect(() => {
    loadInitialListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, region]);

  // Effect B: selectedLocation تغيّر → إعادة ترتيب بالمسافة من الـ pool الموجود (من غير fetch).
  useEffect(() => {
    if (rawPool.length === 0) return;
    recomputeDisplayFromPool(rawPool);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  // Effect B2: hero tab تغيّر.
  // لو النوع المطلوب مش موجود في الـ pool (مثلاً المستخدم كان على rooms وبعدين داس teamup)،
  // نجيب بس الـ collection الناقصة وندمجها. لو الاتنين موجودين → recompute في الميموري بس.
  // أول mount بيتساب لـ Effect A (loadInitialListings) عشان مفيش fetch مزدوج.
  const isFirstHeroEffectRef = useRef(true);
  useEffect(() => {
    if (isFirstHeroEffectRef.current) {
      isFirstHeroEffectRef.current = false;
      return;
    }

    let cancelled = false;

    const run = async () => {
      const needAds = activeHeroFilter === 'all' || activeHeroFilter === 'rooms';
      const needTeamUps = activeHeroFilter === 'all' || activeHeroFilter === 'teamup';
      const missingAds = needAds && !loadedTypesRef.current.ads;
      const missingTeamUps = needTeamUps && !loadedTypesRef.current.teamUps;

      if (!missingAds && !missingTeamUps) {
        if (rawPoolRef.current.length > 0) {
          recomputeDisplayFromPool(rawPoolRef.current);
        }
        return;
      }

      // نجيب بس اللي ناقص: rooms → ads فقط، teamup → team_ups فقط، all → الاتنين لو ناقصين
      const fetchFilter =
        missingAds && missingTeamUps ? 'all' : missingAds ? 'rooms' : 'teamup';

      try {
        const {
          combined,
          nextLastAd,
          nextLastTeamUp,
          adsFetched,
          teamUpsFetched,
          adsHasMore,
          teamUpsHasMore,
        } = await fetchFromFirestore(
          missingAds ? null : lastVisibleAdRef.current,
          missingTeamUps ? null : lastVisibleTeamUpRef.current,
          city,
          region,
          fetchFilter
        );

        if (cancelled) return;

        if (adsFetched) {
          loadedTypesRef.current.ads = true;
          if (adsHasMore !== null) setHasMoreAds(adsHasMore);
          setLastVisibleAd(nextLastAd);
        }
        if (teamUpsFetched) {
          loadedTypesRef.current.teamUps = true;
          if (teamUpsHasMore !== null) setHasMoreTeamUps(teamUpsHasMore);
          setLastVisibleTeamUp(nextLastTeamUp);
        }

        const existingIds = new Set(rawPoolRef.current.map((l) => l.id));
        const uniqueNew = combined.filter((l) => !existingIds.has(l.id));
        const updatedPool = [...rawPoolRef.current, ...uniqueNew];
        setRawPool(updatedPool);
        recomputeDisplayFromPool(updatedPool);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching missing listing types:', err);
          setError(err.message);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHeroFilter]);

  // Effect C: re-fetch expanded category after remount (e.g. closing an ad with X
  // navigates to / and ListingsGrid remounts with empty local expandedListings
  // while App still has expandedCategory set → was showing "No listings").
  useEffect(() => {
    if (!expandedCategory) return;
    if (expandedLoading) return;
    if (expandedRawListings.length > 0 || expandedListings.length > 0) return;
    openExpandedCategory(expandedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedCategory]);

  // ─── Render ───────────────────────────────────────────────────────────────────
  if (expandedCategory) {
    return (
      <>
        <ExpandedCategoryGrid
          title={expandedCategoryTitles(expandedCategory, getLocalizedText)}
          listings={expandedListings}
          loading={expandedLoading}
          error={expandedError}
          page={expandedPage}
          pageSize={EXPANDED_PAGE_SIZE}
          onPageChange={handleExpandedPageChange}
          onBack={closeExpandedCategory}
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          favouriteIds={favouriteIds}
          onToggleFavorite={toggleFavorite}
          categoryKey={expandedCategory}
          activeFilterCount={
            expandedCategory === 'rooms'
              ? countRoomsFilters(roomsExpandedFilters)
              : expandedCategory === 'foundFlat'
                ? countFoundFlatFilters(foundFlatExpandedFilters)
                : expandedCategory === 'searching'
                  ? countSearchingFilters(searchingExpandedFilters)
                  : 0
          }
          onOpenFilters={() => {
            if (expandedCategory === 'rooms') setShowRoomsFilters(true);
            if (expandedCategory === 'foundFlat') setShowFoundFlatFilters(true);
            if (expandedCategory === 'searching') setShowSearchingFilters(true);
          }}
        />
        {expandedCategory === 'rooms' && (
          <RoomsExpandedFilters
            isOpen={showRoomsFilters}
            onClose={() => setShowRoomsFilters(false)}
            currentLanguage={currentLanguage}
            getLocalizedText={getLocalizedText}
            filters={roomsExpandedFilters}
            onApply={handleApplyRoomsFilters}
          />
        )}
        {expandedCategory === 'foundFlat' && (
          <FoundFlatExpandedFilters
            isOpen={showFoundFlatFilters}
            onClose={() => setShowFoundFlatFilters(false)}
            currentLanguage={currentLanguage}
            getLocalizedText={getLocalizedText}
            filters={foundFlatExpandedFilters}
            onApply={handleApplyFoundFlatFilters}
          />
        )}
        {expandedCategory === 'searching' && (
          <SearchingExpandedFilters
            isOpen={showSearchingFilters}
            onClose={() => setShowSearchingFilters(false)}
            currentLanguage={currentLanguage}
            getLocalizedText={getLocalizedText}
            filters={searchingExpandedFilters}
            onApply={handleApplySearchingFilters}
          />
        )}
      </>
    );
  }

  if (loading) {
    return (
      <div className="listings-status-message">
        {getLocalizedText('جاري تحميل الإعلانات...', 'Loading ads...')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="listings-status-message">
        {getLocalizedText('حدث خطأ في تحميل الإعلانات', 'Error loading ads')}
      </div>
    );
  }

  // تقسيم الإعلانات المعروضة 3 صفوف: غرف (regular) / تيم أب لقيت شقة / تيم أب بيدوروا
  const roomListings = displayedListings.filter((l) => l.adType !== 'team_up');
  const foundFlatListings = displayedListings.filter(
    (l) => l.adType === 'team_up' && l.hasFlat === 'yes'
  );
  const searchingListings = displayedListings.filter(
    (l) => l.adType === 'team_up' && l.hasFlat !== 'yes'
  );

  if (roomListings.length === 0 && foundFlatListings.length === 0 && searchingListings.length === 0) {
    return (
      <p className="listings-status-message">
        {getLocalizedText(
          'تعذر العثور على إعلانات تتطابق مع فلاترك.',
          "Couldn't find listings that match your filters."
        )}
      </p>
    );
  }

  return (
    <>
      <ListingsRow
        title={getLocalizedText('الغرف المتاحة في الشقق المشترك', 'Available rooms in shared flats')}
        listings={roomListings}
        rowRef={roomsRowRef}
        onScroll={scrollRow}
        currentLanguage={currentLanguage}
        getLocalizedText={getLocalizedText}
        favouriteIds={favouriteIds}
        onToggleFavorite={toggleFavorite}
        categoryKey="rooms"
        onShowMore={openExpandedCategory}
      />

      <ListingsRow
        title={getLocalizedText('الفرق التي عثرت على شقة للمشاركة و الإيجار', 'Teams that found a flat to share and rent')}
        listings={foundFlatListings}
        rowRef={foundFlatRowRef}
        onScroll={scrollRow}
        currentLanguage={currentLanguage}
        getLocalizedText={getLocalizedText}
        favouriteIds={favouriteIds}
        onToggleFavorite={toggleFavorite}
        categoryKey="foundFlat"
        onShowMore={openExpandedCategory}
      />

      <ListingsRow
        title={getLocalizedText('فرق تبحث عن شقة للمشاركة و الإيجار', 'Teams searching for a flat to share and rent')}
        listings={searchingListings}
        rowRef={searchingRowRef}
        onScroll={scrollRow}
        currentLanguage={currentLanguage}
        getLocalizedText={getLocalizedText}
        favouriteIds={favouriteIds}
        onToggleFavorite={toggleFavorite}
        categoryKey="searching"
        onShowMore={openExpandedCategory}
      />
    </>
  );
}

// ─── صف واحد قابل لإعادة الاستخدام (غرف / تيم أب) ───────────────────────────

function ListingsRow({
  title,
  listings,
  rowRef,
  onScroll,
  currentLanguage,
  getLocalizedText,
  favouriteIds,
  onToggleFavorite,
  categoryKey,
  onShowMore,
}) {
  if (listings.length === 0) return null;

  const visibleListings = listings.slice(0, 7);
  const previewListings = listings.slice(7, 9); // لأول صورتين هيتحطوا في كارت "See all"
  const hasMoreToShow = listings.length > 7;

  return (
    <section className="listings-row-section">
      <div className="listings-row-header">
        <h3 className="listings-row-title">
          {title}
          {'\u00A0'}
          <button
            type="button"
            className="listings-row-show-more-btn"
            onClick={() => onShowMore(categoryKey)}
            aria-label={getLocalizedText('عرض المزيد', 'Show More')}
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </button>
        </h3>

        <div className="listings-row-arrows">
          <button
            type="button"
            className="listings-row-arrow-btn"
            aria-label="prev"
            onClick={() => onScroll(rowRef, -1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="listings-row-arrow-btn"
            aria-label="next"
            onClick={() => onScroll(rowRef, 1)}
          >
            ›
          </button>
        </div>
      </div>

      <div className="listings-row-track" ref={rowRef}>
        {visibleListings.map((listing) => (
          <ListingCard
            key={listing.id}
            listing={listing}
            currentLanguage={currentLanguage}
            getLocalizedText={getLocalizedText}
            favouriteIds={favouriteIds}
            onToggleFavorite={onToggleFavorite}
          />
        ))}

        {hasMoreToShow && (
          <div
            className="listing-see-all-card"
            role="button"
            tabIndex={0}
            onClick={() => onShowMore(categoryKey)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onShowMore(categoryKey);
            }}
          >
            <div className="see-all-cover">
              {previewListings[0] && getCardImage(previewListings[0]) && (
                <img
                  className="see-all-img see-all-img-back"
                  src={getCardImage(previewListings[0])}
                  alt=""
                  loading="lazy"
                />
              )}
              {previewListings[1] && getCardImage(previewListings[1]) && (
                <img
                  className="see-all-img see-all-img-front"
                  src={getCardImage(previewListings[1])}
                  alt=""
                  loading="lazy"
                />
              )}
            </div>
            <span className="see-all-showmore-btn">
              {getLocalizedText('عرض المزيد', 'Show More')}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Helper for multiple regions in Profile Ads ─────────────────────────────
const formatMultiLocation = (city, regionsArray, language) => {
  if (!Array.isArray(regionsArray) || regionsArray.length === 0) {
    return getLocationDisplay(city, regionsArray, language);
  }

  const cityObj = cities.find(c => c.value === city);
  const cityText = language === 'ar'
    ? (cityObj?.ar || city)
    : (cityObj?.en || city);

  const regionTexts = regionsArray.map(regionValue => {
    const regionObj = regions[city]?.find(r => r.value === regionValue);
    return language === 'ar'
      ? (regionObj?.ar || regionValue)
      : (regionObj?.en || regionValue);
  });

  // فاصلة حسب اللغة
  const separator = language === 'ar' ? '، ' : ', ';

  return `${cityText} - ${regionTexts.join(separator)}`;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function calculateDistance(lat1, lon1, lat2, lon2) {
  if (!lat2 || !lon2) return Infinity;
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

export default ListingsGrid;
